import React, { useState, useEffect, useRef } from 'react';
import DisabledFieldset from '../../common/DisabledFieldset';
import {
  Box,
  Typography,
  Button,
  Alert,
  Grid,
  Paper,
  Stack,
  Chip,
  TextField,
  IconButton,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Switch,
  List,
  ListItem,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  MenuItem,
  Tab,
  Tabs
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import TableSchemaViewer from './TableSchemaViewer';
import IngestionRuleBuilder from './IngestionRuleBuilder';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RestoreIcon from '@mui/icons-material/Restore';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ExportSettings from './ExportSettings';
import HelpButton from '../../common/HelpButton';
import { getPeriodType, getPeriodFieldLabel, PERIOD_CONFIG } from '../../../utils/periodUtils';
import { doc, runTransaction } from 'firebase/firestore';
import { assertExternalSchemaCurrent, buildExternalColumnAddition } from '../../../utils/externalColumns';
import { db } from '../../../services/firebase';
import { browseExternalSource } from '../../../services/externalSources';
import { formatTextForDatabase } from '../../common/TextFormatterProvider';
import { isExternalTable, isRawTable } from '../../../utils/tableTypes';
import { POLICY_LABELS, RECENT_WEIGHTED_POLICY, REPORTING_DATE_MODES, policyColumns } from '../../../utils/tableSettings';

// What each table type means for ingestion, shown above the schema so the rest of the page reads
// in context (a time-series table merges each upload into its rows; a raw table appends them).
const TABLE_TYPE_COPY = {
  timeSeries: {
    label: 'Time-series table',
    description: 'Each upload merges values into its reporting period; the table gains a column per period.'
  },
  raw: {
    label: 'Raw table',
    description: 'Every upload appends its rows exactly as cleaned; nothing is merged.'
  }
};

const POLICY_COLUMN_KINDS = { detail: 'Detail', attribute: 'Attribute', metric: 'Metric' };

const REPORTING_DATE_MODE_LABELS = {
  latest: 'Latest reporting period',
  earliest: 'Earliest reporting period',
  most_common: 'Most common reporting period',
};

// Module scope keeps the section wrapper a stable component type, so editing a field inside a
// section never remounts it mid-keystroke. `framed` is off for sections whose content already
// draws its own card.
function SettingsSection({ title, description, actions, framed = true, children }) {
  const heading = (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: description ? 0.5 : 2 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>{title}</Typography>
        {actions}
      </Box>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>
      )}
    </>
  );

  if (!framed) {
    return (
      <Box sx={{ mb: 3 }}>
        {heading}
        {children}
      </Box>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      {heading}
      {children}
    </Paper>
  );
}

function TableSettings({
  currentTable,
  companyName,
  error,
  setError,
  success,
  setSuccess,
  possibleValues,
  newValueInputs,
  handleNewValueInputChange,
  handleAddPossibleValue,
  handleRemovePossibleValue,
  displaySettings,
  handleColumnToggle,
  ingestionRules,
  setIngestionRules,
  saveSettings,
  saving,
  handleDeleteClick,
  displayOrder,
  onDisplayOrderChange,
  showColumnTotals,
  onToggleColumnTotals,
  propagateMostRecent,
  onTogglePropagateMostRecent,
  isHidden,
  onToggleHidden,
  columnPolicies,
  onColumnPoliciesChange,
  reportingDateMode,
  onReportingDateModeChange,
  classification,
  onClassificationChange,
  entityResolution,
  onEntityResolutionChange,
  extractionPrompt,
  onExtractionPromptChange,
  autoAllocateProfile,
  onToggleAutoAllocateProfile,
  onExternalTablePatched,
  exportDraftRef,
  onExportDraftDirtyChange,
}) {
  const [schemaDialogOpen, setSchemaDialogOpen] = useState(false);
  const [schemaValidation, setSchemaValidation] = useState(null);
  const [schemaValidating, setSchemaValidating] = useState(false);
  // A validation or add-column failure, shown inside the dialog — the page's
  // own error banner sits behind the modal where nobody can see it.
  const [schemaError, setSchemaError] = useState('');
  const [addingColumn, setAddingColumn] = useState('');
  const schemaRequestRef = useRef(0);
  // The alias being typed for each class, keyed by `${column}/${class}`.
  const [aliasInputs, setAliasInputs] = useState({});
  useEffect(() => {
    setSchemaDialogOpen(false);
    setSchemaValidation(null);
    setSchemaError('');
    setSchemaValidating(false);
    setAddingColumn('');
    schemaRequestRef.current += 1;
    return () => { schemaRequestRef.current += 1; };
  }, [companyName, currentTable?.id]);
  const [activeTab, setActiveTab] = useState('general');
  // Once opened, the Export tab stays mounted (hidden behind the other tabs) so
  // a mapping draft survives a tab switch; it is only ever unmounted with the table.
  const [exportTabVisited, setExportTabVisited] = useState(false);

  // Every section's content changes with the table, so the previously open tab is meaningless.
  useEffect(() => {
    setActiveTab('general');
    setExportTabVisited(false);
  }, [currentTable?.id]);

  useEffect(() => {
    if (activeTab === 'export') setExportTabVisited(true);
  }, [activeTab]);

  if (!currentTable) {
    return (
      <Box sx={{ p: 3, height: '100%', overflowY: 'auto' }}>
        <Paper sx={{ p: 3, mt: 2, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            Select a table from the sidebar to manage its settings.
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Helpers to compute default order from schema (used when no custom order set)
  const computeDefaultDisplayOrder = (table) => {
    const metadata = [...(table.attributeColumns || []).map(c => c.name), ...(table.tableAttributeColumns || []).map(c => c.name)];
    const attributes = (table.detailColumns || []).map(c => c.name);
    // Value leaves flattened, sorted alphabetically (Parent.Child when nested) to mirror current UI default
    const valueLeaves = [];
    const schema = table.metricColumns?.schema || {};
    const pushLeaf = (path) => { if (path) valueLeaves.push(path); };
    Object.entries(schema).forEach(([colName, colSchema]) => {
      const isLeaf = colSchema && typeof colSchema === 'object' && (
        Object.prototype.hasOwnProperty.call(colSchema, 'updateBehavior') ||
        Object.prototype.hasOwnProperty.call(colSchema, 'type')
      );
      if (isLeaf) {
        pushLeaf(colName);
      } else {
        const nested = (colSchema && typeof colSchema === 'object' && colSchema.schema && typeof colSchema.schema === 'object')
          ? colSchema.schema
          : (colSchema || {});
        Object.keys(nested).forEach(subColName => pushLeaf(`${colName}.${subColName}`));
      }
    });
    // Alphabetical sort: parent first then child
    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
    valueLeaves.sort((a, b) => {
      const [a1, a2] = a.split('.');
      const [b1, b2] = b.split('.');
      const p = collator.compare(a1, b1);
      if (p !== 0) return p;
      return collator.compare(a2 || '', b2 || '');
    });
    return { attribute: metadata, detail: attributes, metric: valueLeaves };
  };

  const effectiveOrder = displayOrder || computeDefaultDisplayOrder(currentTable);
  const isRaw = isRawTable(currentTable);
  const isExternal = isExternalTable(currentTable);
  const tableTypeCopy = TABLE_TYPE_COPY[currentTable.tableType];
  // External tables have no export templates, which would leave that tab empty.
  const showExportTab = !isExternal;
  const selectedTab = (activeTab === 'export' && !showExportTab) ? 'general' : activeTab;

  const moveItem = (arr, index, direction) => {
    const newArr = [...arr];
    const swapWith = direction === 'up' ? index - 1 : index + 1;
    if (swapWith < 0 || swapWith >= newArr.length) return newArr;
    const tmp = newArr[index];
    newArr[index] = newArr[swapWith];
    newArr[swapWith] = tmp;
    return newArr;
  };

  const setOrderSection = (section, newArr) => {
    const base = displayOrder || effectiveOrder; // initialize from effective
    const updated = { ...base, [section]: newArr };
    onDisplayOrderChange(updated);
  };

  const resetToDefault = () => {
    onDisplayOrderChange(null);
  };

  // Helpers to flatten value schema into leaf paths for Mapping Values editor
  const isValueLeaf = (node) => node && typeof node === 'object' && (
    Object.prototype.hasOwnProperty.call(node, 'updateBehavior') ||
    Object.prototype.hasOwnProperty.call(node, 'type')
  );

  // Only a time-series table has metrics; a leaf's mapping key is under 'period.'
  // (whatever the period type).
  const flattenValueLeaves = (schema, prefix = []) => {
    const leaves = [];
    Object.entries(schema || {}).forEach(([key, value]) => {
      if (isValueLeaf(value)) {
        const path = [...prefix, key];
        leaves.push({
          key: `period.${path.join('.')}`,
          label: path.join(' / ').replace(/_/g, ' ')
        });
      } else {
        const nested = (value && typeof value === 'object' && value.schema && typeof value.schema === 'object')
          ? value.schema
          : (value || {});
        leaves.push(...flattenValueLeaves(nested, [...prefix, key]));
      }
    });
    return leaves;
  };

  const handleValidateSchema = async () => {
    if (!currentTable?.id) return;
    const requestId = ++schemaRequestRef.current;
    setSchemaDialogOpen(true);
    setSchemaValidation(null);
    setSchemaValidating(true);
    setSchemaError('');
    try {
      const result = await browseExternalSource({
        action: 'validate_schema',
        table_id: currentTable.id,
      });
      if (schemaRequestRef.current === requestId) setSchemaValidation(result);
    } catch (err) {
      if (schemaRequestRef.current === requestId) {
        setSchemaError(`The source schema could not be checked: ${err.message || 'unknown error'}`);
      }
    } finally {
      if (schemaRequestRef.current === requestId) setSchemaValidating(false);
    }
  };

  const handleAddSchemaColumn = async (remoteColumn) => {
    if (!remoteColumn?.remote || !companyName || !currentTable?.id) return;
    const name = formatTextForDatabase((remoteColumn.proposed_name || remoteColumn.remote).trim());
    if (!name) return;
    const context = schemaValidation?.validation_context;
    const requestId = schemaRequestRef.current;

    try {
      if (!context || context.table_id !== currentTable.id) {
        throw new Error('Validate this table before adding a column.');
      }
      setAddingColumn(remoteColumn.remote);
      setSchemaError('');
      const tableRef = doc(db, `users/${companyName}/tables`, currentTable.id);
      const connectionRef = doc(db, `users/${companyName}/external_connections`, context.connection_id);
      // One transaction per add: the column lists are rebuilt from the document as it is
      // now and the column is pinned in the same write, so two adds in flight append to
      // each other instead of the second undoing the first (M112).
      const patch = await runTransaction(db, async (transaction) => {
        const fresh = (await transaction.get(tableRef)).data();
        const connection = (await transaction.get(connectionRef)).data();
        assertExternalSchemaCurrent(fresh, connection, context);
        const updates = buildExternalColumnAddition(
          fresh,
          remoteColumn,
          name,
          displayOrder || computeDefaultDisplayOrder({ ...currentTable, ...fresh }),
        );
        if (!updates) {
          // Already adopted, by this dialog or another tab: show it as mapped.
          return { detailColumns: fresh.detailColumns || [] };
        }
        transaction.update(tableRef, updates);
        return updates;
      });
      onExternalTablePatched?.(patch);
    } catch (err) {
      if (schemaRequestRef.current === requestId) {
        setSchemaValidation(null);
        setSchemaError(`The column ${name} could not be added: ${err.message || 'unknown error'}`);
      }
    } finally {
      if (schemaRequestRef.current === requestId) setAddingColumn('');
    }
  };

  return (
    <DisabledFieldset disabled={saving} sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 3 }}>
        <Typography variant="h5" gutterBottom>
          {currentTable?.name} Settings
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
            Settings saved successfully!
          </Alert>
        )}

        <Tabs
          value={selectedTab}
          onChange={(_, value) => setActiveTab(value)}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
        >
          <Tab value="general" label="General" />
          <Tab value="ingestion" label="Ingestion" />
          {showExportTab && <Tab value="export" label="Export" />}
        </Tabs>

        {selectedTab === 'general' && (
          <>
            {/* General Settings */}
            <SettingsSection
              title="General Settings"
              description="Basic behaviour for this table."
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <VisibilityOffIcon fontSize="small" color={isHidden ? 'warning' : 'disabled'} />
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      Hide Table
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Hidden tables are moved to a separate section in the sidebar and are not included in the AI assistant's context.
                    </Typography>
                  </Box>
                </Box>
                <Switch
                  checked={!!isHidden}
                  onChange={() => onToggleHidden(currentTable.id)}
                  color="warning"
                />
              </Box>
            </SettingsSection>

            {/* Schema Section */}
            <SettingsSection
              title="Schema"
              description="The table's structure: its identifier, columns, and how each column updates on ingestion."
              actions={
                <>
                  {isExternal && currentTable.schema_drift && (
                    <Chip label="Schema drift" color="warning" size="small" />
                  )}
                  <HelpButton
                    docPath="data-page/working-with-tables/viewing-schema"
                    tooltip="Schema Documentation"
                  />
                </>
              }
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Chip label={tableTypeCopy.label} size="small" color="primary" variant="outlined" />
                <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                  {tableTypeCopy.description}
                </Typography>
                {isExternal && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleValidateSchema}
                    disabled={schemaValidating}
                    sx={{ flexShrink: 0 }}
                  >
                    {schemaValidating ? 'Validating...' : 'Validate Schema'}
                  </Button>
                )}
              </Box>
              <TableSchemaViewer
                schema={currentTable}
                metricOrder={currentTable.display_order?.metric || displayOrder?.metric || null}
              />
            </SettingsSection>

            {/* Column Policies (Spec 6 section 2.3, Spec 3 section 3) */}
            {!isRaw && !isExternal && columnPolicies && (
              <SettingsSection
                title="Column Policies"
                description="How the table chooses a column's value when files disagree."
              >
                <Stack spacing={1.5}>
                  {policyColumns(currentTable).map(({ column, kind, choices }) => {
                    const entry = columnPolicies[column] || {};
                    return (
                      <Box key={column} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {column.replace(/_/g, ' ')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {POLICY_COLUMN_KINDS[kind]}
                          </Typography>
                        </Box>
                        <TextField
                          select
                          size="small"
                          label="Policy"
                          value={entry.policy || ''}
                          onChange={(e) => {
                            const policy = e.target.value;
                            const changed = policy === RECENT_WEIGHTED_POLICY
                              ? { policy, half_life_days: null }
                              : { policy };
                            onColumnPoliciesChange({ ...columnPolicies, [column]: changed });
                          }}
                          sx={{ minWidth: 280 }}
                        >
                          {choices.map((policy) => (
                            <MenuItem key={policy} value={policy}>{POLICY_LABELS[policy]}</MenuItem>
                          ))}
                        </TextField>
                        {entry.policy === RECENT_WEIGHTED_POLICY && (
                          <TextField
                            size="small"
                            type="number"
                            label="Half-life (days)"
                            value={entry.half_life_days ?? ''}
                            onChange={(e) => onColumnPoliciesChange({
                              ...columnPolicies,
                              [column]: {
                                policy: entry.policy,
                                half_life_days: e.target.value === '' ? null : Number(e.target.value),
                              },
                            })}
                            sx={{ width: 160 }}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              </SettingsSection>
            )}

            {/* Classes (Spec 6 section 2.3, Spec 2 section 4.4): aliases only */}
            {!isExternal && classification && Object.keys(classification).length > 0 && (
              <SettingsSection
                title="Classes"
                description="The classes each classified column's values are sorted into, and their aliases."
              >
                <Stack spacing={2}>
                  {Object.entries(classification).map(([column, configuration]) => (
                    <Paper key={column} variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {column.replace(/_/g, ' ')} (threshold {Math.round(configuration.threshold * 100)}%)
                      </Typography>
                      <Stack spacing={1.5}>
                        {Object.entries(configuration.classes).map(([className, aliases]) => {
                          const inputKey = `${column}/${className}`;
                          const typed = (aliasInputs[inputKey] || '').trim();
                          const setAliases = (newAliases) => onClassificationChange({
                            ...classification,
                            [column]: {
                              ...configuration,
                              classes: { ...configuration.classes, [className]: newAliases },
                            },
                          });
                          return (
                            <Box key={className}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>{className}</Typography>
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ my: 0.5 }}>
                                {aliases.map((alias, index) => (
                                  <Chip
                                    key={`${alias}-${index}`}
                                    label={alias}
                                    size="small"
                                    onDelete={() => setAliases(aliases.filter((_, position) => position !== index))}
                                    sx={{ mb: 0.5 }}
                                  />
                                ))}
                              </Stack>
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <TextField
                                  size="small"
                                  placeholder="Add alias"
                                  value={aliasInputs[inputKey] || ''}
                                  onChange={(e) => setAliasInputs(prev => ({ ...prev, [inputKey]: e.target.value }))}
                                  sx={{ flexGrow: 1, maxWidth: 400 }}
                                />
                                <IconButton
                                  size="small"
                                  color="primary"
                                  aria-label={`Add an alias to ${className}`}
                                  disabled={!typed}
                                  onClick={() => {
                                    setAliases([...aliases, typed]);
                                    setAliasInputs(prev => ({ ...prev, [inputKey]: '' }));
                                  }}
                                >
                                  <AddIcon />
                                </IconButton>
                              </Box>
                            </Box>
                          );
                        })}
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </SettingsSection>
            )}

            {/* Display Columns Settings */}
            <SettingsSection
              title="Display Columns"
              description="Which columns the data view shows, and the order they appear in."
              actions={
                <HelpButton
                  docPath="data-page/working-with-tables/table-settings/display-columns"
                  tooltip="Display Columns Documentation"
                />
              }
            >
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" gutterBottom>
                    {isRaw ? 'Columns' : 'Details Columns'}
                  </Typography>
                  <FormGroup>
                    {currentTable.detailColumns?.map((column) => (
                      <FormControlLabel
                        key={column.name}
                        control={
                          <Checkbox
                            checked={displaySettings[currentTable.id]?.[column.name] ?? true}
                            onChange={() => handleColumnToggle(currentTable.id, column.name)}
                          />
                        }
                        label={column.name.replace(/_/g, ' ')}
                      />
                    ))}
                  </FormGroup>
                </Grid>

                <Grid item xs={12} md={4}>
                  {currentTable.attributeColumns.length > 0 && (
                    <>
                      <Typography variant="subtitle2" gutterBottom>
                        Attribute Columns
                      </Typography>
                      <FormGroup>
                        {currentTable.attributeColumns.map((column) => (
                          <FormControlLabel
                            key={column.name}
                            control={
                              <Checkbox
                                checked={displaySettings[currentTable.id]?.[column.name] ?? true}
                                onChange={() => handleColumnToggle(currentTable.id, column.name)}
                              />
                            }
                            label={column.name.replace(/_/g, ' ')}
                          />
                        ))}
                      </FormGroup>
                    </>
                  )}
                  {currentTable.tableAttributeColumns?.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Table Attribute Columns</Typography>
                      {currentTable.tableAttributeColumns.map((column) => (
                        <Typography key={column.name} variant="body2">
                          {column.name} = {column.value} ({column.type})
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Grid>

                <Grid item xs={12} md={4}>
                  {currentTable.metricColumns && (
                    <>
                      <Typography variant="subtitle2" gutterBottom>
                        Metrics Columns
                      </Typography>
                      <FormGroup>
                        {Object.entries(currentTable.metricColumns.schema || {}).map(([colName, colSchema]) => {
                          const isLeaf = colSchema && typeof colSchema === 'object' && (
                            Object.prototype.hasOwnProperty.call(colSchema, 'updateBehavior') ||
                            Object.prototype.hasOwnProperty.call(colSchema, 'type')
                          );

                          if (isLeaf) {
                            // Metrics are a time-series table's: 'period' prefix (works for all period types)
                            const valueColKey = `period.${colName}`;
                            return (
                              <FormControlLabel
                                key={valueColKey}
                                control={
                                  <Checkbox
                                    checked={displaySettings[currentTable.id]?.[valueColKey] ?? true}
                                    onChange={() => handleColumnToggle(currentTable.id, valueColKey)}
                                  />
                                }
                                label={colName.replace(/_/g, ' ')}
                              />
                            );
                          }

                          // Nested structure: prefer schema property if present, otherwise iterate keys of object
                          const nested = (colSchema && typeof colSchema === 'object' && colSchema.schema && typeof colSchema.schema === 'object')
                            ? colSchema.schema
                            : (colSchema || {});
                          const subKeys = Object.keys(nested);

                          return (
                            <Box key={colName} sx={{ ml: 2, mb: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {colName.replace(/_/g, ' ')}
                              </Typography>
                              {subKeys.map((subColName) => {
                                // 'period' prefix (works for all period types)
                                const valueColKey = `period.${colName}.${subColName}`;
                                return (
                                  <FormControlLabel
                                    key={valueColKey}
                                    control={
                                      <Checkbox
                                        checked={displaySettings[currentTable.id]?.[valueColKey] ?? true}
                                        onChange={() => handleColumnToggle(currentTable.id, valueColKey)}
                                      />
                                    }
                                    label={`${colName.replace(/_/g, ' ')} // ${subColName.replace(/_/g, ' ')}`}
                                    sx={{ ml: 2 }}
                                  />
                                );
                              })}
                            </Box>
                          );
                        })}
                      </FormGroup>
                      <Box sx={{ mt: 2 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={!!(propagateMostRecent?.[currentTable.id])}
                              onChange={() => onTogglePropagateMostRecent(currentTable.id)}
                              color="primary"
                            />
                          }
                          label={`Propagate most recent ${PERIOD_CONFIG[getPeriodType(currentTable)]?.label?.toLowerCase() || 'period'} values`}
                        />
                      </Box>
                    </>
                  )}
                  {/* Outside the metrics block: a raw or external table has no metric
                      columns at all, and since its numeric data columns are now totalled
                      it needs the switch that turns them off. */}
                  <Box sx={{ mt: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={showColumnTotals?.[currentTable.id] ?? true}
                          onChange={() => onToggleColumnTotals(currentTable.id)}
                          color="primary"
                        />
                      }
                      label="Add column totals"
                    />
                  </Box>
                </Grid>
              </Grid>

              {/* Column Order Section */}
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Column Order
                </Typography>
                <Button size="small" startIcon={<RestoreIcon />} onClick={resetToDefault}>
                  Use default order
                </Button>
              </Box>
              <Grid container spacing={2}>
                {!isRaw && (
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Identifier</Typography>
                      <Chip label={currentTable.identifier?.name || 'row id'} size="small" />
                    </Paper>
                  </Grid>
                )}
                <Grid item xs={12} md={4}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Attribute</Typography>
                    <List dense>
                      {effectiveOrder.attribute.map((name, idx) => (
                        <ListItem key={name}
                          secondaryAction={
                            <Box>
                              <IconButton size="small" onClick={() => setOrderSection('attribute', moveItem(effectiveOrder.attribute, idx, 'up'))} disabled={idx === 0}>
                                <ArrowUpwardIcon fontSize="inherit" />
                              </IconButton>
                              <IconButton size="small" onClick={() => setOrderSection('attribute', moveItem(effectiveOrder.attribute, idx, 'down'))} disabled={idx === effectiveOrder.attribute.length - 1}>
                                <ArrowDownwardIcon fontSize="inherit" />
                              </IconButton>
                            </Box>
                          }
                        >
                          <ListItemText primary={name.replace(/_/g, ' ')} primaryTypographyProps={{ variant: 'body2' }} />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>{isRaw ? 'Columns' : 'Details'}</Typography>
                    <List dense>
                      {effectiveOrder.detail.map((name, idx) => (
                        <ListItem key={name}
                          secondaryAction={
                            <Box>
                              <IconButton size="small" onClick={() => setOrderSection('detail', moveItem(effectiveOrder.detail, idx, 'up'))} disabled={idx === 0}>
                                <ArrowUpwardIcon fontSize="inherit" />
                              </IconButton>
                              <IconButton size="small" onClick={() => setOrderSection('detail', moveItem(effectiveOrder.detail, idx, 'down'))} disabled={idx === effectiveOrder.detail.length - 1}>
                                <ArrowDownwardIcon fontSize="inherit" />
                              </IconButton>
                            </Box>
                          }
                        >
                          <ListItemText primary={name.replace(/_/g, ' ')} primaryTypographyProps={{ variant: 'body2' }} />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    {(() => {
                      const tableType = currentTable.tableType;
                      const periodLabel = PERIOD_CONFIG[getPeriodType(currentTable)]?.label?.toLowerCase() || 'period';
                      return (
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                          {tableType === 'timeSeries' ? `Metric (applies within each ${periodLabel})` : 'Metric'}
                        </Typography>
                      );
                    })()}
                    <List dense>
                      {effectiveOrder.metric.map((name, idx) => (
                        <ListItem key={`${name}-${idx}`}
                          secondaryAction={
                            <Box>
                              <IconButton size="small" onClick={() => setOrderSection('metric', moveItem(effectiveOrder.metric, idx, 'up'))} disabled={idx === 0}>
                                <ArrowUpwardIcon fontSize="inherit" />
                              </IconButton>
                              <IconButton size="small" onClick={() => setOrderSection('metric', moveItem(effectiveOrder.metric, idx, 'down'))} disabled={idx === effectiveOrder.metric.length - 1}>
                                <ArrowDownwardIcon fontSize="inherit" />
                              </IconButton>
                            </Box>
                          }
                        >
                          <ListItemText primary={name.replace(/_/g, ' ')} secondary={name.includes('.') ? `${name.split('.')[0]} • ${name.split('.')[1]}` : null} primaryTypographyProps={{ variant: 'body2' }} />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Grid>
              </Grid>
            </SettingsSection>
          </>
        )}

        {selectedTab === 'ingestion' && (
          <>
            {/* Auto Allocation */}
            {!isExternal && (
              <SettingsSection
                title="Auto Allocation"
                description="Skip the allocation approval step for smart uploads whose data profile match is unambiguous."
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      Auto-allocate to data profile
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      When an uploaded file matches exactly one data profile — or matches none and this
                      table has only one profile — Decode approves the allocation automatically. Files
                      that match several profiles equally, or whose profile is missing a required
                      attribute value, still wait for review.
                    </Typography>
                  </Box>
                  <Switch
                    checked={!!autoAllocateProfile}
                    onChange={() => onToggleAutoAllocateProfile(currentTable.id)}
                    color="primary"
                  />
                </Box>
              </SettingsSection>
            )}

            {/* Possible Values Management */}
            {!isExternal && (
              <SettingsSection
                title="Mapping Values"
                description="Configure possible values for automatic field mapping during file uploads"
                actions={
                  <HelpButton
                    docPath="data-page/uploading-data/reviewing-uploads/how-cleaning-works"
                    tooltip="Mapping Values Documentation"
                  />
                }
              >
                {/* Identifier */}
                {currentTable.identifier?.name && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                      Identifier
                    </Typography>
                    {(() => {
                      const columnName = currentTable.identifier.name;
                      const columnValues = possibleValues[currentTable.id]?.[columnName] || [];
                      const inputKey = `${currentTable.id}-${columnName}`;

                      return (
                        <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'background.default' }}>
                          <Typography variant="subtitle2" gutterBottom>
                            {columnName.replace(/_/g, ' ')}
                          </Typography>

                          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                            {columnValues.map((value, index) => (
                              <Chip
                                key={index}
                                label={value || '(empty)'}
                                onDelete={() => handleRemovePossibleValue(currentTable.id, columnName, index)}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ mb: 1 }}
                              />
                            ))}
                          </Stack>

                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <TextField
                              size="small"
                              placeholder="Add mapping value"
                              value={newValueInputs[inputKey] || ''}
                              onChange={(e) => handleNewValueInputChange(currentTable.id, columnName, e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && handleAddPossibleValue(currentTable.id, columnName)}
                              sx={{ flexGrow: 1, maxWidth: 400 }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => handleAddPossibleValue(currentTable.id, columnName)}
                              color="primary"
                              disabled={!newValueInputs[inputKey]?.trim()}
                            >
                              <AddIcon />
                            </IconButton>
                          </Box>
                        </Paper>
                      );
                    })()}
                  </Box>
                )}

                {/* Details Columns */}
                {currentTable.detailColumns?.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>{isRaw ? 'Columns' : 'Details Columns'}</Typography>
                    <Grid container spacing={2}>
                      {currentTable.detailColumns.map((column) => {
                        const columnName = column.name;
                        const columnValues = possibleValues[currentTable.id]?.[columnName] || [];
                        const inputKey = `${currentTable.id}-${columnName}`;
                        return (
                          <Grid item xs={12} md={6} key={columnName}>
                            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                              <Typography variant="subtitle2" gutterBottom>{columnName.replace(/_/g, ' ')}</Typography>
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 1, minHeight: 32 }}>
                                {columnValues.map((value, index) => (
                                  <Chip
                                    key={index}
                                    label={value || '(empty)'}
                                    onDelete={() => handleRemovePossibleValue(currentTable.id, columnName, index)}
                                    size="small"
                                    sx={{ mb: 0.5 }}
                                  />
                                ))}
                              </Stack>
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <TextField
                                  size="small"
                                  placeholder="Add value"
                                  value={newValueInputs[inputKey] || ''}
                                  onChange={(e) => handleNewValueInputChange(currentTable.id, columnName, e.target.value)}
                                  onKeyPress={(e) => e.key === 'Enter' && handleAddPossibleValue(currentTable.id, columnName)}
                                  sx={{ flexGrow: 1 }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => handleAddPossibleValue(currentTable.id, columnName)}
                                  color="primary"
                                  disabled={!newValueInputs[inputKey]?.trim()}
                                >
                                  <AddIcon />
                                </IconButton>
                              </Box>
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                )}

                {/* Period Field - only for timeSeries tables */}
                {currentTable.tableType === 'timeSeries' && (() => {
                  const periodType = getPeriodType(currentTable);
                  const periodFieldLabel = getPeriodFieldLabel(periodType);
                  const periodConfig = PERIOD_CONFIG[periodType];
                  const columnName = 'period';
                  const columnValues = possibleValues[currentTable.id]?.[columnName] || [];
                  const inputKey = `${currentTable.id}-${columnName}`;
                  return (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>{periodFieldLabel}</Typography>
                      <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                        <Typography variant="subtitle2" gutterBottom>
                          {periodConfig?.label || 'Period'} (format: {periodConfig?.example || 'YYYY_MM'})
                        </Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 1, minHeight: 32 }}>
                          {columnValues.map((value, index) => (
                            <Chip
                              key={index}
                              label={value || '(empty)'}
                              onDelete={() => handleRemovePossibleValue(currentTable.id, columnName, index)}
                              size="small"
                              sx={{ mb: 0.5 }}
                            />
                          ))}
                        </Stack>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <TextField
                            size="small"
                            placeholder={`Add ${periodConfig?.label?.toLowerCase() || 'period'} mapping value`}
                            value={newValueInputs[inputKey] || ''}
                            onChange={(e) => handleNewValueInputChange(currentTable.id, columnName, e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleAddPossibleValue(currentTable.id, columnName)}
                            sx={{ flexGrow: 1 }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => handleAddPossibleValue(currentTable.id, columnName)}
                            color="primary"
                            disabled={!newValueInputs[inputKey]?.trim()}
                          >
                            <AddIcon />
                          </IconButton>
                        </Box>
                      </Paper>
                    </Box>
                  );
                })()}

                {/* Metrics Columns */}
                {currentTable.metricColumns?.schema && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Metrics Columns</Typography>
                    <Grid container spacing={2}>
                      {flattenValueLeaves(currentTable.metricColumns.schema).map((item) => {
                        const columnName = item.key;
                        const columnValues = possibleValues[currentTable.id]?.[columnName] || [];
                        const inputKey = `${currentTable.id}-${columnName}`;
                        return (
                          <Grid item xs={12} md={6} key={columnName}>
                            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                              <Typography variant="subtitle2" gutterBottom>{item.label}</Typography>
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 1, minHeight: 32 }}>
                                {columnValues.map((value, index) => (
                                  <Chip
                                    key={index}
                                    label={value || '(empty)'}
                                    onDelete={() => handleRemovePossibleValue(currentTable.id, columnName, index)}
                                    size="small"
                                    sx={{ mb: 0.5 }}
                                  />
                                ))}
                              </Stack>
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <TextField
                                  size="small"
                                  placeholder="Add value or formula"
                                  value={newValueInputs[inputKey] || ''}
                                  onChange={(e) => handleNewValueInputChange(currentTable.id, columnName, e.target.value)}
                                  onKeyPress={(e) => e.key === 'Enter' && handleAddPossibleValue(currentTable.id, columnName)}
                                  sx={{ flexGrow: 1 }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => handleAddPossibleValue(currentTable.id, columnName)}
                                  color="primary"
                                  disabled={!newValueInputs[inputKey]?.trim()}
                                >
                                  <AddIcon />
                                </IconButton>
                              </Box>
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                )}
              </SettingsSection>
            )}

            {/* Extraction Prompt Section */}
            <SettingsSection
              title="Extraction Prompt"
              description={
                <>
                  Optional instructions for the document extraction model, applied to every PDF or
                  Word upload into this table — both when it discovers the document's layout and when
                  it extracts the rows. Data profiles can add their own prompt on top of this one.
                </>
              }
            >
              <TextField
                multiline
                minRows={3}
                fullWidth
                placeholder="e.g. Dates in these documents are always day-first (DD/MM/YYYY)."
                value={extractionPrompt || ''}
                onChange={(e) => onExtractionPromptChange(e.target.value)}
                inputProps={{ maxLength: 4000 }}
              />
            </SettingsSection>

            {/* Ingestion Rules */}
            {!isExternal && (
              <SettingsSection
                title="Ingestion Rules"
                description="Row and cell rules applied to every file ingested into this table."
                framed={false}
                actions={
                  <HelpButton
                    docPath="data-page/working-with-tables/table-settings/ingestion-rules"
                    tooltip="Ingestion Rules Documentation"
                  />
                }
              >
                <IngestionRuleBuilder
                  rules={ingestionRules[currentTable.id] || {}}
                  tableSchema={currentTable}
                  onRulesChange={(newRules) => {
                    setIngestionRules(prev => ({
                      ...prev,
                      [currentTable.id]: newRules
                    }));
                  }}
                />
              </SettingsSection>
            )}

            {/* Reporting Date (Spec 6 section 2.3) */}
            {!isExternal && (
              <SettingsSection
                title="Reporting Date"
                description="Which reporting period a file reports on when its rows span several."
              >
                <TextField
                  select
                  size="small"
                  label="Reporting date"
                  value={reportingDateMode || ''}
                  onChange={(e) => onReportingDateModeChange(e.target.value)}
                  sx={{ minWidth: 240 }}
                >
                  {REPORTING_DATE_MODES.map((mode) => (
                    <MenuItem key={mode} value={mode}>{REPORTING_DATE_MODE_LABELS[mode]}</MenuItem>
                  ))}
                </TextField>
              </SettingsSection>
            )}

            {/* Entity Resolution (Spec 3 section 4.3) */}
            {!isRaw && !isExternal && (
              <SettingsSection
                title="Entity Resolution"
                description="How alike two rows' details must be to count as the same entity."
              >
                {/* Any number above 0 and at most 1 (Spec 3 section 3), so the field
                    imposes no step; Save Settings refuses anything else. */}
                <TextField
                  size="small"
                  type="number"
                  label="Similarity threshold (above 0, at most 1)"
                  value={entityResolution?.threshold ?? ''}
                  onChange={(e) => onEntityResolutionChange({
                    threshold: e.target.value === '' ? null : Number(e.target.value),
                  })}
                  inputProps={{ min: 0, max: 1, step: 'any' }}
                  sx={{ maxWidth: 280 }}
                />
              </SettingsSection>
            )}
          </>
        )}

        {showExportTab && exportTabVisited && (
          <Box sx={{ display: selectedTab === 'export' ? 'block' : 'none' }}>
            <SettingsSection
              title="Export Settings"
              description="Reusable export templates for this table's data."
              actions={
                <HelpButton
                  docPath="data-page/working-with-tables/table-settings/export-settings"
                  tooltip="Export Settings Documentation"
                />
              }
            >
              <ExportSettings
                key={`${companyName}/${currentTable.id}`}
                companyId={companyName}
                table={currentTable}
                disabled={saving}
                draftRef={exportDraftRef}
                onDraftDirtyChange={onExportDraftDirtyChange}
              />
            </SettingsSection>
          </Box>
        )}
      </Box>

      {/* Save and delete stay reachable from every tab, outside the scroll area so they never
          sit on top of the settings they act on. */}
      <Box
        sx={{
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          backgroundColor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteClick}
          >
            Delete Table
          </Button>
          {isExternal && (
            <Typography variant="body2" color="text.secondary">
              Removes this table from Decode. Your source data is not touched.
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={saveSettings}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>

      <Dialog open={schemaDialogOpen} onClose={() => setSchemaDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Validate Schema</DialogTitle>
        <DialogContent dividers>
          {schemaError && !schemaValidating && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={!schemaValidation ? (
                <Button color="inherit" size="small" onClick={handleValidateSchema}>Try again</Button>
              ) : null}
            >
              {schemaError}
            </Alert>
          )}
          {schemaValidating ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Checking source schema...</Typography>
            </Box>
          ) : schemaValidation ? (
            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>New columns</Typography>
                {(schemaValidation.new || []).length > 0 ? (
                  <List dense>
                    {(schemaValidation.new || []).map((column) => {
                      const name = formatTextForDatabase((column.proposed_name || column.remote || '').trim());
                      const exists = (currentTable.detailColumns || []).some(existing => existing.name === name);
                      const canAdd = column.mappable !== false && !!name && !exists;
                      return (
                        <ListItem
                          key={column.remote}
                          secondaryAction={
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleAddSchemaColumn(column)}
                              disabled={!canAdd || Boolean(addingColumn)}
                            >
                              {addingColumn === column.remote ? 'Adding...' : 'Add'}
                            </Button>
                          }
                        >
                          <ListItemText
                            primary={`${column.remote} -> ${name || 'Unnamed'}`}
                            secondary={`${column.native_type || 'Unknown'}${column.proposed_type ? ` as ${column.proposed_type}` : ''}${column.mappable === false ? ' - not mappable' : ''}${exists ? ' - already mapped' : ''}`}
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">No new columns.</Typography>
                )}
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Missing mapped columns</Typography>
                {(schemaValidation.missing || []).length > 0 ? (
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {(schemaValidation.missing || []).map((name) => (
                      <Chip key={name} label={name} color="warning" size="small" sx={{ mb: 1 }} />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">No mapped columns are missing.</Typography>
                )}
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Retyped columns</Typography>
                {(schemaValidation.retyped || []).length > 0 ? (
                  <List dense>
                    {(schemaValidation.retyped || []).map((item) => (
                      <ListItem key={item.remote}>
                        <ListItemText
                          primary={item.remote}
                          secondary={`${item.was || 'Unknown'} -> ${item.now || 'Unknown'}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">No type changes detected.</Typography>
                )}
              </Box>
            </Stack>
          ) : !schemaError && (
            <Typography variant="body2" color="text.secondary">No validation result yet.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSchemaDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </DisabledFieldset>
  );
}

export default TableSettings;
